import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import Icon from '../common/Icons';
import receptionService from '../../services/receptionService';
import externalMemberService from '../../services/externalMemberService';
import { MEMBER_TYPES, ORIENT_LABELS } from '../receptionist/receptionConstants';
import { formatVisitDate, visitStatusLabel } from '../receptionist/receptionFormatters';
import {
  formatMemberAge,
  formatMemberSexeShort,
  formatPersonProfileMeta,
  memberDisplayName
} from '../receptionist/browse/browseFormatters';
import {
  ReceptionSearchAnchor,
  ReceptionFloatingResults,
  ReceptionFloatingResultItem
} from '../receptionist/ui';
import NurseIntakeConfirmModal from '../nurse/forms/NurseIntakeConfirmModal';
import { buildNurseIntakeConsultation } from '../nurse/utils/nurseIntakeConsultation';
import { useDoctorWorkspace } from './doctorWorkspaceContext';

const WORKSPACE_CONFIG = {
  physician: {
    serviceLine: 'MEDICAL',
    cardEyebrow: 'Accueil patient',
    cardTitle: 'Accueillir un adhérent',
    subtitle: 'Matricule, nom, prénom ou téléphone — orientation médecine automatique.',
    subtitleSite: 'identifiez le patient puis accueillez-le en consultation médicale.',
    selectedHint: 'Patient identifié. Vérifiez la fiche puis accueillez-le en consultation médicale.',
    welcomeButton: 'Accueillir en médecine',
    successMessage: 'Passage enregistré · patient ajouté à la file médecine.',
    modalTitle: 'Accueillir en consultation médicale',
    modalSubtitle: (siteName) =>
      siteName
        ? `Passage MEDICAL sur ${siteName} — visible dans la file consultations.`
        : 'Orientation médecine automatique.',
    notesPlaceholder: 'Réception depuis poste médical…',
    symptomsPlaceholder: 'Symptômes ou motif de la visite…'
  },
  headDoctor: {
    serviceLine: 'MEDICAL',
    cardEyebrow: 'Accueil patient',
    cardTitle: 'Accueillir un adhérent',
    subtitle: 'Matricule, nom, prénom ou téléphone — orientation médecine automatique.',
    subtitleSite: 'identifiez le patient puis accueillez-le en consultation médicale.',
    selectedHint: 'Patient identifié. Vérifiez la fiche puis accueillez-le en consultation médicale.',
    welcomeButton: 'Accueillir en médecine',
    successMessage: 'Passage enregistré · patient ajouté à la file médecine.',
    modalTitle: 'Accueillir en consultation médicale',
    modalSubtitle: (siteName) =>
      siteName
        ? `Passage MEDICAL sur ${siteName} — visible dans la file consultations.`
        : 'Orientation médecine automatique.',
    notesPlaceholder: 'Réception depuis poste médical…',
    symptomsPlaceholder: 'Symptômes ou motif de la visite…'
  },
  dentist: {
    serviceLine: 'DENTAL',
    cardEyebrow: 'Accueil patient',
    cardTitle: 'Accueillir un patient',
    subtitle: 'Matricule, nom, prénom ou téléphone — orientation dentaire automatique.',
    subtitleSite: 'identifiez le patient puis accueillez-le en consultation dentaire.',
    selectedHint: 'Patient identifié. Vérifiez la fiche puis accueillez-le en consultation dentaire.',
    welcomeButton: 'Accueillir en dentaire',
    successMessage: 'Passage enregistré · patient ajouté à la file dentaire.',
    modalTitle: 'Accueillir en consultation dentaire',
    modalSubtitle: (siteName) =>
      siteName
        ? `Passage DENTAL sur ${siteName} — visible dans la file consultations.`
        : 'Orientation dentaire automatique.',
    notesPlaceholder: 'Réception depuis poste dentaire…',
    symptomsPlaceholder: 'Douleur, soin attendu ou motif de la visite…'
  }
};

const BASE_COPY = {
  searchLabel: 'Recherche patient',
  searchPlaceholder: 'Matricule, nom, prénom ou téléphone…',
  searchButton: 'Rechercher',
  searching: 'Recherche',
  searchMin: 'Saisissez au moins 2 caractères (matricule, nom ou prénom).',
  searchEmpty: 'Aucun résultat pour cette recherche',
  searchError: 'Erreur de recherche',
  clearSearch: 'Effacer la recherche patient',
  hintSearch: 'La recherche se lance automatiquement. Sélectionnez un résultat pour afficher la fiche.',
  smieResults: 'Adhérents SMIE',
  externalResults: 'Membres externes',
  select: 'Sélectionner',
  patientIdentified: 'Patient identifié',
  changePatient: 'Changer',
  recentEyebrow: 'Dernières activités',
  recentTitle: '5 derniers passages',
  recentLoading: 'Chargement des derniers passages…',
  noRecent: 'Aucun passage récent pour ce patient.',
  historyError: 'Historique récent indisponible.',
  externalHistory: 'Historique automatique disponible pour les adhérents avec matricule.',
  noHistory: 'Historique indisponible sans matricule.',
  noSite: 'Sélectionnez d’abord votre site de travail dans la barre supérieure.',
  invalidSite: 'Site invalide.',
  intakeError: 'Enregistrement impossible.'
};

export default function DoctorIntakePanel({ onIntakeSuccess, className = '' }) {
  const { workSites, workSiteId, toast, clinicalWorkspace } = useDoctorWorkspace();
  const config = WORKSPACE_CONFIG[clinicalWorkspace] || WORKSPACE_CONFIG.physician;
  const siteName = useMemo(
    () => workSites.find((site) => String(site.id) === String(workSiteId))?.name || '',
    [workSiteId, workSites]
  );

  const [memberSearch, setMemberSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [externalMatches, setExternalMatches] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);
  const [recentActivitiesMessage, setRecentActivitiesMessage] = useState('');
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');
  const [orientSymptoms, setOrientSymptoms] = useState('');
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);

  const siteReady = workSiteId !== null && workSiteId !== undefined && String(workSiteId).trim() !== '';

  const getPatientDisplayName = useCallback((kind, member) => {
    const name = `${member?.prenom || ''} ${member?.nom || ''}`.trim();
    return name || member?.nom || 'Patient';
  }, []);

  const loadPatientRecentActivities = useCallback(async (kind, member) => {
    if (kind === 'SMIE' && member?.matricule) {
      setRecentActivitiesLoading(true);
      setRecentActivitiesMessage('');
      try {
        const historyRes = await receptionService.getVisitHistoryByMatricule(member.matricule, { limit: 5 });
        const rows = historyRes.success && Array.isArray(historyRes.data) ? historyRes.data : [];
        setRecentActivities(rows);
        setRecentActivitiesMessage(rows.length === 0 ? BASE_COPY.noRecent : '');
      } catch (historyError) {
        setRecentActivities([]);
        setRecentActivitiesMessage(historyError.message || BASE_COPY.historyError);
      } finally {
        setRecentActivitiesLoading(false);
      }
      return;
    }

    setRecentActivities([]);
    setRecentActivitiesLoading(false);
    setRecentActivitiesMessage(kind === 'EXTERNAL' ? BASE_COPY.externalHistory : BASE_COPY.noHistory);
  }, []);

  const resetPatientSelection = useCallback(() => {
    setSelectedPatient(null);
    setRecentActivities([]);
    setRecentActivitiesMessage('');
    setRecentActivitiesLoading(false);
  }, []);

  const selectPatient = useCallback(
    async (kind, member) => {
      setSelectedPatient({ kind, member });
      setSearchResultsOpen(false);
      setSearchError('');
      setMemberSearch(getPatientDisplayName(kind, member));
      await loadPatientRecentActivities(kind, member);
    },
    [getPatientDisplayName, loadPatientRecentActivities]
  );

  const changeSelectedPatient = useCallback(() => {
    resetPatientSelection();
    setMemberSearch('');
    setMatches([]);
    setExternalMatches([]);
    setSearchResultsOpen(false);
    setSearchError('');
    setSearchLoading(false);
  }, [resetPatientSelection]);

  const performPatientSearch = useCallback(
    async (rawQuery, { notifyEmpty = false } = {}) => {
      setSearchError('');
      const q = rawQuery.trim();
      if (q.length < 2) {
        if (notifyEmpty) setSearchError(BASE_COPY.searchMin);
        setSearchResultsOpen(false);
        setMatches([]);
        setExternalMatches([]);
        return;
      }
      setSearchLoading(true);
      setSearchResultsOpen(true);
      try {
        const res = await receptionService.searchMembers(q, { limit: 30 });
        const extRes = await externalMemberService.list({ search: q, limit: 30, offset: 0 });
        const list = res.success ? res.data || [] : [];
        const extList = extRes.success ? extRes.data || [] : [];
        setMatches(list);
        setExternalMatches(extList);
        if (notifyEmpty && list.length === 0 && extList.length === 0) {
          toast('warning', BASE_COPY.searchEmpty);
        }
      } catch (err) {
        setSearchError(err.message || BASE_COPY.searchError);
        setMatches([]);
        setExternalMatches([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [toast]
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    await performPatientSearch(memberSearch, { notifyEmpty: true });
  };

  useEffect(() => {
    const query = memberSearch.trim();
    if (query.length < 2) {
      setSearchResultsOpen(false);
      setMatches([]);
      setExternalMatches([]);
      setSearchLoading(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      performPatientSearch(query);
    }, 320);

    return () => window.clearTimeout(timer);
  }, [memberSearch, performPatientSearch]);

  const clearPatientSearch = () => changeSelectedPatient();

  const openIntakeModal = () => {
    if (!siteReady) {
      toast('warning', BASE_COPY.noSite);
      return;
    }
    if (!selectedPatient) return;
    setVisitNotes('');
    setOrientSymptoms('');
    setShowIntakeModal(true);
  };

  const submitIntake = async () => {
    if (!selectedPatient || !siteReady) return;
    const sid = parseInt(String(workSiteId), 10);
    if (!Number.isInteger(sid) || sid < 1) {
      toast('danger', BASE_COPY.invalidSite);
      return;
    }

    const patientSnapshot = selectedPatient;

    setIntakeSubmitting(true);
    try {
      const payload = {
        site_id: sid,
        service_line: config.serviceLine,
        symptoms: orientSymptoms.trim() || undefined,
        notes: visitNotes.trim() || undefined,
        ...(patientSnapshot.kind === 'SMIE'
          ? { member_id: patientSnapshot.member.id }
          : { external_member_id: patientSnapshot.member.id })
      };
      const res = await receptionService.registerAndOrientVisit(payload);
      if (!res.success) {
        throw new Error(res.message || BASE_COPY.intakeError);
      }

      setShowIntakeModal(false);
      toast('success', config.successMessage);

      const consultation = buildNurseIntakeConsultation({
        response: res,
        patientSnapshot,
        siteId: sid,
        getPatientDisplayName,
        serviceLine: config.serviceLine
      });

      changeSelectedPatient();
      onIntakeSuccess?.(consultation);
    } catch (err) {
      toast('danger', err.message || BASE_COPY.intakeError);
    } finally {
      setIntakeSubmitting(false);
    }
  };

  const selectedPatientProfile = useMemo(() => {
    if (!selectedPatient) return null;
    const { kind, member } = selectedPatient;
    const displayName = getPatientDisplayName(kind, member);
    const typeLabel =
      kind === 'EXTERNAL'
        ? member.statut || 'Membre externe'
        : MEMBER_TYPES[member.type] || member.type || 'Adhérent SMIE';
    const details = [
      member.matricule ? `Matricule ${member.matricule}` : null,
      member.company_name || null,
      typeLabel,
      member.telephone || null,
      formatMemberSexeShort(member.sexe) !== '—' ? formatMemberSexeShort(member.sexe) : null,
      formatMemberAge(member.date_naissance) !== '—' ? formatMemberAge(member.date_naissance) : null
    ].filter(Boolean);

    return { displayName, typeLabel, details };
  }, [getPatientDisplayName, selectedPatient]);

  const patientSearchOpen = searchResultsOpen && memberSearch.trim().length >= 2;

  return (
    <>
      <Card
        className={`border-0 recv-dash-card recv-dash-card--elevated recv-dash-card--primary recv-intake-card nr-intake-card ${className}${
          patientSearchOpen ? ' is-search-open' : ''
        }`}
      >
        <Card.Header className="recv-dash-card__head recv-intake-card__head border-0 bg-transparent mb-0">
          <span className="recv-dash-card__eyebrow">{config.cardEyebrow}</span>
          <Card.Title className="h5 mb-0 fw-semibold mt-2 text-body">{config.cardTitle}</Card.Title>
          <Card.Subtitle className="text-muted small mt-2 mb-0 fw-normal">
            {siteName ? (
              <>
                Site <strong>{siteName}</strong> — {config.subtitleSite}
              </>
            ) : (
              config.subtitle
            )}
          </Card.Subtitle>
        </Card.Header>
        <Card.Body className="recv-intake-card__body">
          {!siteReady ? (
            <p className="text-muted small mb-0">{BASE_COPY.noSite}</p>
          ) : (
            <Form onSubmit={handleSearch} className="recv-intake-form">
              <div className="recv-intake-form__topline">
                <Form.Label className="recv-intake-form__label mb-0">{BASE_COPY.searchLabel}</Form.Label>
                {memberSearch.trim().length >= 2 && (
                  <span className="recv-intake-form__count">
                    {matches.length + externalMatches.length} résultat(s)
                  </span>
                )}
              </div>
              <div className="recv-intake-search-row">
                <ReceptionSearchAnchor className="recv-intake-search">
                  <span className="recv-intake-search__icon" aria-hidden>
                    <Icon name="search" size={18} />
                  </span>
                  <Form.Control
                    className="recv-intake-search__input"
                    value={memberSearch}
                    onChange={(ev) => {
                      const value = ev.target.value;
                      if (
                        selectedPatient &&
                        value.trim() !== getPatientDisplayName(selectedPatient.kind, selectedPatient.member)
                      ) {
                        resetPatientSelection();
                        setMatches([]);
                        setExternalMatches([]);
                      }
                      setMemberSearch(value);
                      if (value.trim().length < 2) {
                        setSearchResultsOpen(false);
                        setSearchError('');
                      } else {
                        setSearchResultsOpen(true);
                      }
                    }}
                    placeholder={BASE_COPY.searchPlaceholder}
                    autoComplete="off"
                    aria-controls="doctor-intake-member-search-list"
                    aria-expanded={searchResultsOpen}
                    disabled={!siteReady}
                  />
                  {memberSearch ? (
                    <button
                      type="button"
                      className="recv-intake-search__clear"
                      onClick={clearPatientSearch}
                      aria-label={BASE_COPY.clearSearch}
                    >
                      <Icon name="times" size={14} />
                    </button>
                  ) : null}
                  <ReceptionFloatingResults
                    listId="doctor-intake-member-search-list"
                    show={patientSearchOpen}
                    loading={searchLoading}
                    emptyMessage={BASE_COPY.searchEmpty}
                  >
                    {matches.length > 0 ? (
                      <>
                        <div className="recv-floating-results__title">{BASE_COPY.smieResults}</div>
                        {matches.map((member) => (
                          <ReceptionFloatingResultItem
                            key={member.id}
                            primary={memberDisplayName(member)}
                            secondary={member.company_name || undefined}
                            meta={formatPersonProfileMeta({
                              matricule: member.matricule,
                              typeLabel: MEMBER_TYPES[member.type] || member.type,
                              sexe: member.sexe,
                              date_naissance: member.date_naissance
                            })}
                            actionLabel={BASE_COPY.select}
                            selected={
                              selectedPatient?.kind === 'SMIE' &&
                              String(selectedPatient.member.id) === String(member.id)
                            }
                            onSelect={() => selectPatient('SMIE', member)}
                          />
                        ))}
                      </>
                    ) : null}
                    {externalMatches.length > 0 ? (
                      <>
                        <div className="recv-floating-results__title">{BASE_COPY.externalResults}</div>
                        {externalMatches.map((member) => (
                          <ReceptionFloatingResultItem
                            key={`ext-${member.id}`}
                            primary={memberDisplayName(member) || member.nom}
                            meta={formatPersonProfileMeta({
                              leading: [member.telephone, member.statut || 'EXTERNE'].filter(Boolean),
                              sexe: member.sexe,
                              date_naissance: member.date_naissance
                            })}
                            actionLabel={BASE_COPY.select}
                            selected={
                              selectedPatient?.kind === 'EXTERNAL' &&
                              String(selectedPatient.member.id) === String(member.id)
                            }
                            onSelect={() => selectPatient('EXTERNAL', member)}
                          />
                        ))}
                      </>
                    ) : null}
                  </ReceptionFloatingResults>
                </ReceptionSearchAnchor>
                <Button type="submit" variant="primary" className="recv-intake-search__submit" disabled={searchLoading || !siteReady}>
                  {searchLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {BASE_COPY.searching}
                    </>
                  ) : (
                    BASE_COPY.searchButton
                  )}
                </Button>
              </div>
              <div className="recv-intake-search__hint">
                {selectedPatient ? config.selectedHint : BASE_COPY.hintSearch}
              </div>
              {searchError ? <div className="text-danger small mt-2">{searchError}</div> : null}

              {selectedPatient && selectedPatientProfile ? (
                <div className="recv-patient-card" aria-live="polite">
                  <div className="recv-patient-card__head">
                    <div className="recv-patient-card__identity">
                      <span className="recv-patient-card__badge">{BASE_COPY.patientIdentified}</span>
                      <h3 className="recv-patient-card__name">{selectedPatientProfile.displayName}</h3>
                      <p className="recv-patient-card__details mb-0">
                        {selectedPatientProfile.details.join(' · ')}
                      </p>
                    </div>
                    <button type="button" className="recv-patient-card__change" onClick={changeSelectedPatient}>
                      {BASE_COPY.changePatient}
                    </button>
                  </div>

                  <div className="recv-recent-activity recv-patient-card__history">
                    <div className="recv-recent-activity__head">
                      <div>
                        <span className="recv-recent-activity__eyebrow">{BASE_COPY.recentEyebrow}</span>
                        <h4 className="recv-recent-activity__title mb-0">{BASE_COPY.recentTitle}</h4>
                      </div>
                      {recentActivities.length > 0 ? (
                        <span className="recv-recent-activity__count">{recentActivities.length}/5</span>
                      ) : null}
                    </div>
                    {recentActivitiesLoading ? (
                      <div className="recv-recent-activity__loading">
                        <Spinner animation="border" size="sm" />
                        <span>{BASE_COPY.recentLoading}</span>
                      </div>
                    ) : recentActivities.length > 0 ? (
                      <div className="recv-recent-activity__list">
                        {recentActivities.map((activity) => (
                          <article key={activity.id} className="recv-recent-activity__item">
                            <div className="recv-recent-activity__date">{formatVisitDate(activity.registered_at)}</div>
                            <div className="recv-recent-activity__body">
                              <div className="recv-recent-activity__main">
                                {activity.service_line
                                  ? ORIENT_LABELS[activity.service_line] || activity.service_line
                                  : 'Passage réception'}
                              </div>
                              <div className="recv-recent-activity__meta">
                                {[activity.company_name, activity.visit_site_name, activity.registered_by_name]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </div>
                            </div>
                            <span
                              className={`recv-recent-activity__status ${
                                activity.status === 'ORIENTED' ? 'is-ok' : 'is-pending'
                              }`}
                            >
                              {visitStatusLabel(activity.status)}
                            </span>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="recv-recent-activity__empty mb-0">
                        {recentActivitiesMessage || BASE_COPY.noRecent}
                      </p>
                    )}
                  </div>

                  <div className="recv-patient-card__actions">
                    <Button variant="primary" onClick={openIntakeModal}>
                      {config.welcomeButton}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Form>
          )}
        </Card.Body>
      </Card>

      <NurseIntakeConfirmModal
        show={showIntakeModal}
        onHide={() => setShowIntakeModal(false)}
        saving={intakeSubmitting}
        onSubmit={submitIntake}
        patientProfile={selectedPatientProfile}
        siteName={siteName}
        title={config.modalTitle}
        subtitle={config.modalSubtitle(siteName)}
        submitLabel={config.welcomeButton}
        visitNotes={visitNotes}
        onVisitNotesChange={setVisitNotes}
        visitNotesPlaceholder={config.notesPlaceholder}
        orientSymptoms={orientSymptoms}
        onOrientSymptomsChange={setOrientSymptoms}
        orientSymptomsPlaceholder={config.symptomsPlaceholder}
      />
    </>
  );
}
